import React, { useState } from 'react';
import apiClient, { getViewAs, startViewAs, stopViewAs, getAccessToken } from '../services/apiClient';

// VIEW-AS v3: admin only. Floating button "View as" -> pick "All Ref. Parties" (normal admin view) or any Ref. Party
// (a login user is NOT needed) -> the software reloads exactly as that Ref. Party sees it (read-only).
// While viewing, a red bar shows who is viewed, with Switch and Exit.
const BOX = { position: 'fixed', zIndex: 3000, background: 'var(--color-surface, #fff)', color: 'var(--color-text-primary, #111)', border: '1px solid var(--color-border, #ccc)', borderRadius: 10, boxShadow: '0 6px 24px rgba(0,0,0,0.25)' };
const ROW = { display: 'block', width: '100%', textAlign: 'left', padding: '8px 6px', border: 'none', borderBottom: '1px solid var(--color-border, #eee)', background: 'transparent', color: 'inherit', cursor: 'pointer' };
const CACHE = 'viewAsList3';
// The list is kept for this tab so that "Switch" works while viewing as someone (the list itself is admin-only).
const readCache = () => { try { const x = sessionStorage.getItem(CACHE); return x ? JSON.parse(x) : null; } catch (e) { return null; } };
const writeCache = (l) => { try { sessionStorage.setItem(CACHE, JSON.stringify(l)); } catch (e) { /* ignore */ } };

export default function ViewAsControl({ user }) {
  const va = getViewAs();
  const [open, setOpen] = useState(false);
  const [list, setList] = useState(readCache);
  const [q, setQ] = useState('');
  const [err, setErr] = useState('');
  const code = user && user.role && user.role.code;
  const isAdmin = ['admin', 'super_admin'].includes(code);
  // VIEW-AS v3.2: only for a really logged-in user (user loaded AND an access token held), and never on a login-type page
  const authPage = /^\/(login|signin|sign-in|forgot|reset|verify)/i.test((typeof window !== 'undefined' && window.location && window.location.pathname) || '');
  if (!user || !getAccessToken() || authPage) return null;
  if (!va && !isAdmin) return null;

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next && !va) {
      setErr('');
      apiClient.get('/auth/view-as-users')
        .then((res) => { const items = res.data.data.items; setList(items); writeCache(items); })
        .catch((e) => setErr((e && e.message) || 'Could not load Ref. Parties'));
    }
  };
  const byParty = new Map();
  (list || []).forEach((u) => {
    if (u.rpId == null) return;
    if (!byParty.has(u.rpId)) byParty.set(u.rpId, { rpId: u.rpId, refParty: u.refParty, users: [] });
    if (u.name) byParty.get(u.rpId).users.push(u.name);
  });
  const parties = [...byParty.values()];
  const s = q.trim().toLowerCase();
  const shown = parties.filter((p) => !s || `${p.refParty || ''} ${p.users.join(' ')}`.toLowerCase().includes(s));
  const allActive = !va;
  const isCurrent = (p) => Boolean(va && va.party && String(va.id) === String(p.rpId));

  const panel = open && (
    <div style={{ ...BOX, ...(va ? { left: '50%', transform: 'translateX(-50%)' } : { right: 14 }), bottom: 58, width: 320, maxHeight: 440, padding: 10, display: 'flex', flexDirection: 'column', gap: 8 }}>
      <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search Ref. Party / user" style={{ padding: '6px 10px', border: '1px solid var(--color-border, #ccc)', borderRadius: 6 }} />
      {err && <div style={{ color: '#B42318' }}>{err}</div>}
      {!err && list === null && <div>Loading…</div>}
      <div style={{ overflowY: 'auto' }}>
        <button type="button" onClick={() => { if (va) stopViewAs(); else setOpen(false); }}
          style={{ ...ROW, background: allActive ? 'var(--color-accent-soft, #E8F0FE)' : 'transparent' }}>
          <b>🌐 All Ref. Parties</b>
          <div style={{ fontSize: 12, opacity: 0.7 }}>Normal admin view — everything{parties.length ? ` (${parties.length} Ref. Parties)` : ''}{allActive ? ' · current' : ''}</div>
        </button>
        {shown.map((p) => (
          <button key={p.rpId} type="button" onClick={() => startViewAs(p.rpId, p.refParty, true)}
            style={{ ...ROW, background: isCurrent(p) ? 'var(--color-accent-soft, #E8F0FE)' : 'transparent' }}>
            <b>{p.refParty}</b>
            <div style={{ fontSize: 12, opacity: 0.7 }}>{p.users.length ? p.users.join(', ') : 'no login user'}{isCurrent(p) ? ' · current' : ''}</div>
          </button>
        ))}
        {list !== null && shown.length === 0 && <div>No Ref. Party found.</div>}
      </div>
    </div>
  );

  if (va) {
    return (
      <>
        {panel}
        <div style={{ ...BOX, left: '50%', bottom: 14, transform: 'translateX(-50%)', background: '#B42318', color: '#fff', border: 'none', padding: '8px 14px', display: 'flex', gap: 12, alignItems: 'center' }}>
          <span>Viewing as <b>{va.name || `user #${va.id}`}</b> (read-only)</span>
          <button type="button" onClick={toggle} style={{ border: 'none', borderRadius: 6, padding: '4px 12px', fontWeight: 700, cursor: 'pointer' }}>Switch</button>
          <button type="button" onClick={stopViewAs} style={{ border: 'none', borderRadius: 6, padding: '4px 12px', fontWeight: 700, cursor: 'pointer' }}>Exit</button>
        </div>
      </>
    );
  }
  return (
    <>
      <button type="button" onClick={toggle} title="See the software as a Ref. Party sees it"
        style={{ ...BOX, right: 14, bottom: 14, padding: '8px 14px', cursor: 'pointer', fontWeight: 700 }}>👁 View as</button>
      {panel}
    </>
  );
}
